import {
  Button,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  Spinner,
} from '@fluentui/react-components'
import { Delete24Regular, Eye24Regular, Navigation24Regular } from '@fluentui/react-icons'
import { useState } from 'react'
import { toast } from 'react-toastify'
import { useTokenValidator } from '@/hooks/useTokenValidator'
import { useSessionStorage } from '@/hooks/useSessionStorage'
import { createRestClient } from '@/contexts/QueryProvider'
import { ENDPOINTS } from '@/util'

type ImageActionsMenuProps = {
  imageKey: string
  onDelete: () => void
}

export function ImageActionsMenu({ imageKey, onDelete }: ImageActionsMenuProps) {
  // Get the token from session storage
  const [token] = useSessionStorage('token', '')

  // Use the token validator
  const { isTokenExpired } = useTokenValidator()

  // Get the image URL
  const imageUrl = `${ENDPOINTS.DOTNET}/rest/support/getImage?imageKey=${imageKey}`

  // Use the API client to delete an image
  const restClient = createRestClient()
  const { mutateAsync: deleteImage, isPending: isDeleting } = restClient.deleteSupportApiDeleteImage.useMutation()

  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [isImageDialogOpen, setImageDialogOpen] = useState(false)

  // Handle opening the view image dialog
  const handleViewImage = () => {
    if (!token || isTokenExpired()) {
      toast.error('Your session has expired. Please log in again.')
      return
    }
    setImageDialogOpen(true)
  }

  // Handle the delete action
  const handleDeleteImage = async () => {
    try {
      await deleteImage({
        query: { imageKey },
      })
      toast.success('Image successfully deleted.')
      setDeleteDialogOpen(false)
      onDelete()
    } catch (error) {
      console.error('Error deleting image:', error)
      toast.error('Error deleting image.')
    }
  }

  return (
    <div>
      {/* Menu Button */}
      <Menu>
        <MenuTrigger disableButtonEnhancement>
          <Button icon={<Navigation24Regular />} />
        </MenuTrigger>
        <MenuPopover>
          <MenuList>
            {/* View Image */}
            <MenuItem onClick={handleViewImage} icon={<Eye24Regular />}>
              View Image
            </MenuItem>

            {/* Delete Image */}
            <MenuItem onClick={() => setDeleteDialogOpen(true)} icon={<Delete24Regular />} disabled={isDeleting}>
              {isDeleting ? <Spinner size="small" label="Deleting..." /> : 'Delete Image'}
            </MenuItem>
          </MenuList>
        </MenuPopover>
      </Menu>

      {/* View Image Dialog */}
      <Dialog open={isImageDialogOpen} onOpenChange={(_, data) => setImageDialogOpen(data.open)}>
        <DialogSurface>
          <DialogBody>
            {imageUrl ? (
              <img src={imageUrl} alt={imageKey} style={{ maxWidth: '100%', maxHeight: '500px' }} />
            ) : (
              <p>No image available</p>
            )}
          </DialogBody>
          <DialogActions>
            <Button onClick={() => setImageDialogOpen(false)}>Close</Button>
          </DialogActions>
        </DialogSurface>
      </Dialog>

      {/* Delete Image Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={(_, data) => setDeleteDialogOpen(data.open)}>
        <DialogSurface>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogBody>
            <p>Are you sure you want to delete this image?</p>
          </DialogBody>
          <DialogActions>
            <Button appearance="secondary" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button appearance="primary" disabled={isDeleting} onClick={handleDeleteImage}>
              {isDeleting ? <Spinner size="small" label="Deleting..." /> : 'Confirm'}
            </Button>
          </DialogActions>
        </DialogSurface>
      </Dialog>
    </div>
  )
}
